import { Floorplan } from '../types/floorplan';
import { RoomProps } from '../types/room';
import firebase from 'firebase/app';
import isEqual from 'lodash.isequal';
import { getLastUpdated } from '../shared/logTools';
import { setSnackMessage } from './ui';
import {
  FLOOR_PLANS_CONFIG_DOC_ID,
  floorPlansArrayToConfigMap,
  queueFloorPlansConfigDocWrite,
  resolveSurveyFloorPlansConfig,
} from './floorplans-config';

export const onSaveFloor =
  ({ newFloorName, prevFloorName }) =>
  async (dispatch, getState, { getFirebase, getFirestore }) => {
    const firestore = getFirestore();

    const { client, survey } = getState().firestore.data;

    const clientDocRef = firestore.collection('clients').doc(client.id);
    const surveyDocRef = clientDocRef.collection('surveys').doc(survey.id);
    const floorPlansConfigDocRef = surveyDocRef
      .collection('config')
      .doc(FLOOR_PLANS_CONFIG_DOC_ID);

    if (!survey) {
      throw new Error('Survey does not exist');
    }
    // check if the floor assigned to the room is a new one, if so add it to the floors array
    const existingFloors = survey.roomSchedule?.floors || [];

    const floorsWithOldRemoved = existingFloors.filter(
      (f) => f !== prevFloorName,
    );

    const floorsWithNewAdded = [...floorsWithOldRemoved, newFloorName];

    const existingRooms = survey.roomSchedule?.data || [];
    const floorPlansConfigSnap = await floorPlansConfigDocRef.get();
    const floorPlansConfigData = floorPlansConfigSnap.exists
      ? floorPlansConfigSnap.data()
      : {};
    const { floorPlans: existingFloorPlans } = resolveSurveyFloorPlansConfig({
      surveyDocData: survey,
      floorPlansConfigData,
      context: 'onSaveFloor',
      surveyId: survey.id,
    });

    const roomScheduleDataArrayUpdated = existingRooms.map(
      (room: RoomProps) => {
        if (room.floor === prevFloorName) {
          return {
            ...room,
            floor: newFloorName,
          };
        }
        return room;
      },
    );

    const floorPlansDataUpdated = existingFloorPlans.map(
      (floorPlan: Floorplan) => {
        if (floorPlan.floor === prevFloorName) {
          return {
            ...floorPlan,
            floor: newFloorName,
          };
        }
        return floorPlan;
      },
    );
    const existingFloorPlansByFloor =
      floorPlansArrayToConfigMap(existingFloorPlans);
    const nextFloorPlansByFloor = floorPlansArrayToConfigMap(
      floorPlansDataUpdated,
    );
    const floorPlansChanged = !isEqual(
      existingFloorPlansByFloor,
      nextFloorPlansByFloor,
    );

    const batch = firestore.batch();

    const roomsOnFloor = await surveyDocRef
      .collection('rooms')
      .where('floor', '==', prevFloorName)
      .get();

    if (roomsOnFloor.size > 0) {
      roomsOnFloor.docs.forEach((room) => {
        batch.update(room.ref, { floor: newFloorName });
      });
    }

    batch.update(surveyDocRef, {
      'roomSchedule.data': roomScheduleDataArrayUpdated,
      'roomSchedule.floors': floorsWithNewAdded,
      floorPlans: floorPlansDataUpdated,
      ...(floorPlansChanged
        ? { floorPlansVersion: firebase.firestore.FieldValue.increment(1) }
        : {}),
      ...getLastUpdated(),
    });
    queueFloorPlansConfigDocWrite({
      batch,
      floorPlansConfigDocRef,
      floorPlansByFloor: nextFloorPlansByFloor,
      lastUpdated: getLastUpdated(),
    });

    batch.commit();

    dispatch(
      setSnackMessage({ message: `${newFloorName} saved successfully` }),
    );
  };
